import React, { useEffect, useState } from "react";
import {
  Save,
  X,
  Info,
  CheckCircle,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import axios from "axios";
import heartLoading from "../../assests/heartLoading.gif";

function DimensionWeightage({ assetId }) {
  const [selectAll, setSelectAll] = useState(false);
  const [dimensions, setDimensions] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock API calls (replace with actual axios calls)
  // Fetch all dimensions and current weightage on mount/assetId change
  useEffect(() => {
    if (!assetId) return;
    const BASE_URL = process.env.REACT_APP_API_URL;
    setLoading(true);
    setError(null);

    // Fetch all dimensions
    axios
      .get(`${BASE_URL}/rule/getDimensions`)
      .then((res) => {
        setDimensions(res.data.dimensions || []);
      })
      .catch(() => setError("Failed to fetch dimensions."));

    // Fetch current weightage
    axios
      .get(`${BASE_URL}/rule/assetInfo/${assetId}`)
      .then((res) => {
        const weightage = res.data.weightage || {};
        // Map to array of {id, name, value}
        setSelectedDimensions(
          Object.entries(weightage).map(([name, value]) => ({
            name,
            value,
            id:
              (res.data.columns || []).find(
                (col) => col.attribute_name === name
              )?.attribute_id || name, // fallback to name
          }))
        );
      })
      .catch(() => setError("Failed to fetch current weightage."))
      .finally(() => setLoading(false));
  }, [assetId]);

  const getTotalProgress = () =>
    selectedDimensions.reduce((acc, d) => acc + Number(d.value || 0), 0);

  const onDimensionSelect = (dimension, checked) => {
    if (checked) {
      setSelectedDimensions((prev) => {
        if (prev.some((d) => d.name === dimension.name)) return prev;
        return [...prev, { ...dimension, value: 0 }];
      });
    } else {
      setSelectedDimensions((prev) =>
        prev.filter((d) => d.name !== dimension.name)
      );
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedDimensions([]);
    } else {
      setSelectedDimensions(
        dimensions
          .filter((d) => d.name !== "drift")
          .map((d) => ({ ...d, value: 0 }))
      );
    }
    setSelectAll(!selectAll);
  };

  const setDimensionPercent = (value, id) => {
    setSelectedDimensions((prev) =>
      prev.map((d) => (d.id === id ? { ...d, value: Math.min(value, 100) } : d))
    );
  };

  const saveDimensions = async () => {
    const weightage = {};
    selectedDimensions.forEach((d) => {
      weightage[d.name] = Number(d.value);
    });
    if (getTotalProgress() !== 100) {
      setError("Total weightage must be 100%.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const BASE_URL = process.env.REACT_APP_API_URL;
      await axios.post(`${BASE_URL}/rule/updateWeightage`, {
        data_asset_id: assetId,
        weightage,
      });
      setError(null);
      // Show success message
      setError("Weightage updated successfully!");
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      setError("Failed to save weightage.");
    } finally {
      setLoading(false);
    }
  };

  const CustomAlert = ({ type, message, onClose }) => {
    const baseClasses =
      "flex items-center gap-3 p-4 rounded-xl border-l-4 transition-all duration-300";
    const typeClasses = {
      success: "bg-emerald-50 border-emerald-500 text-emerald-800",
      error: "bg-red-50 border-red-500 text-red-800",
      warning: "bg-amber-50 border-amber-500 text-amber-800",
    };

    const Icon =
      type === "success" ? CheckCircle : type === "error" ? X : AlertTriangle;
    const iconColor =
      type === "success"
        ? "text-emerald-600"
        : type === "error"
        ? "text-red-600"
        : "text-amber-600";

    return (
      <div className={`${baseClasses} ${typeClasses[type]}`}>
        <Icon className={`h-5 ${iconColor} flex-shrink-0`} />
        <span className="font-medium flex-1">{message}</span>
        {onClose && (
          <button
            onClick={onClose}
            className={`${iconColor} hover:opacity-70 transition-opacity`}
          >
            <X className=" h-4" />
          </button>
        )}
      </div>
    );
  };

  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-12 text-blue-600 animate-spin" />
        <p className="text-gray-600 font-medium">Loading...</p>
      </div>
    </div>
  );

  const ProgressBar = ({ value }) => (
    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div
        className={`h-full transition-all duration-500 rounded-full ${
          value === 100 ? "bg-emerald-500" : "bg-red-500"
        }`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* {loading && <div className="fixed inset-0 bg-white flex items-center justify-center">
                   <div className="flex flex-col items-center space-y-3">
                     <img src={heartLoading} alt="Loading..." className="h-30" />
                     <span className="text-gray-700 text-[20px]">
                     Hang tight! The resources are loading...
                     </span>
                   </div>
                 </div>} */}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Info className=" h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">
              Dimension Weightage
            </h1>
          </div>
          <p className="text-gray-600 leading-relaxed max-w-4xl text-lg">
            Dimension Weightage is a key component in Data Quality (DQ)
            management, enabling teams to prioritize different quality
            dimensions based on business needs. It ensures that data quality
            assessments reflect the importance of each dimension relative to
            overall data integrity and usability.
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className=" h-6 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Why Dimension Weightage is Important
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Different business processes prioritize data quality dimensions
              differently. For example: A real-time analytics system may assign
              high weightage to <strong>Timeliness</strong>. A master data
              management system may focus more on <strong>Uniqueness</strong>{" "}
              and <strong>Completeness</strong>. By assigning weights,
              organizations can <strong>customize</strong> their DQ evaluation
              framework.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className=" h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Configuring Dimension Weightage
              </h2>
            </div>
            <ul className="text-gray-600 leading-relaxed space-y-2">
              <li>
                • The total weightage must sum up to <strong>100%</strong> to
                ensure a balanced assessment.
              </li>
              <li>
                • If certain dimensions are more critical, they should be given
                a <strong>higher percentage</strong>.
              </li>
              <li>
                • The system should not allow saving until the weightage
                distribution meets the <strong>100% requirement</strong>.
              </li>
            </ul>
          </div>
        </div>

        {/* Main Configuration */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Left Side - Dimension Selection */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="border-b-4 border-[#FF03F3] p-3 bg-slate-50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <CheckCircle className=" h-6 text-[#FF03F3]" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Select Dimensions
                </h3>
              </div>
            </div>

            <div className="p-2">
              <label className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer mb-2 border border-gray-200">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className=" h-5 text-[#FF03F3] rounded focus:ring-2 focus:ring-[#FF03F3] focus:ring-offset-2"
                />
                <span className="font-semibold text-gray-700 text-lg">
                  Select All
                </span>
              </label>

              <hr className="mb-3 border-gray-200" />

              <div className="space-y-3">
                {dimensions.map((dimension) => {
                  if (dimension.name !== "drift") {
                    const isSelected = selectedDimensions.some(
                      (item) => item.name === dimension.name
                    );
                    return (
                      <label
                        key={dimension.id}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer border border-transparent hover:border-gray-200"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            onDimensionSelect(dimension, e.target.checked)
                          }
                          className=" h-5 text-[#FF03F3] rounded focus:ring-2 focus:ring-[#FF03F3] focus:ring-offset-2 bg-[#FF03F3]"
                        />
                        <span className="font-medium text-gray-700 capitalize text-lg">
                          {dimension.name}
                        </span>
                      </label>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>

          {/* Right Side - Weightage Configuration */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="border-b-4 border-[#FF03F3] p-3 bg-slate-50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Save className=" h-6 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Selected Dimensions
                </h3>
              </div>
            </div>

            <div className="p-6">
              {selectedDimensions.length > 0 ? (
                <div className="space-y-4">
                  {selectedDimensions.map((dimension) => (
                    <div
                      key={dimension.id}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 border border-gray-200 hover:border-[#FF03F3] transition-colors"
                    >
                      <span className="font-semibold text-gray-800 capitalize text-lg">
                        {dimension.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={dimension.value || 0}
                          onChange={(e) =>
                            setDimensionPercent(
                              Number(e.target.value),
                              dimension.id
                            )
                          }
                          min="0"
                          max="100"
                          className="w-20 px-3 py-2 text-center font-semibold border-2 border-gray-300 rounded-lg focus:border-[#FF03F3] focus:ring-2 focus:ring-[#FF03F3] outline-none transition-all"
                        />
                        <span className="text-gray-600 font-semibold text-lg">
                          %
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Info className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No dimensions selected</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Total Weightage Section - Separate from Selected Dimensions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-3">
          <div className="border-b-4 border-[#FF03F3] p-4 bg-slate-50 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <Info className="h-6 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">
                Total Weightage
              </h3>
            </div>
          </div>

          <div className="p-4">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600 font-medium">Progress</span>
                <span
                  className={`font-bold text-lg ${
                    getTotalProgress() === 100
                      ? "text-emerald-600"
                      : "text-red-600"
                  }`}
                >
                  {getTotalProgress()}% / 100%
                </span>
              </div>
              <ProgressBar value={getTotalProgress()} />
            </div>

            {getTotalProgress() !== 100 ? (
              <CustomAlert
                type="warning"
                message="Total weightage must be 100%. Adjust values to proceed."
              />
            ) : (
              <CustomAlert
                type="success"
                message="Perfect! Total weightage equals 100%. You can now save your configuration."
              />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          {/* <button
            className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
            onClick={() => {}}
          >
            <X className=" h-5" />
            Cancel
          </button> */}
          <button
            onClick={saveDimensions}
            disabled={getTotalProgress() !== 100 || loading}
            className="flex items-center gap-2 px-6 py-3 bg-[#FF03F3] text-white font-semibold rounded-xl hover:bg-[#FF03F3] disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:transform hover:-translate-y-0.5"
          >
            {loading ? (
              <Loader2 className="h-5 animate-spin" />
            ) : (
              <Save className=" h-5" />
            )}
            {loading ? "Saving..." : "Save Configuration"}
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mt-6">
            <CustomAlert
              type={error.includes("successfully") ? "success" : "error"}
              message={error}
              onClose={() => setError(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DimensionWeightage;
